import React, { useState } from 'react';
import { Typography } from '@/lib/typography';
import { Upload, X, FileText, CircleNotch, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ingestArticle } from '@/lib/api';
import { toast } from 'sonner';
import { Dropzone } from '@/components/ui/dropzone';

export default function IngestionModal({ open, onOpenChange, onSuccess }) {
    const [file, setFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const handleFileSelect = (selectedFile) => {
        if (selectedFile) {
            if (!selectedFile.name.endsWith('.docx')) {
                setError('Only .docx files are supported.');
                setFile(null);
                return;
            }
            setFile(selectedFile);
            setError(null);
        }
    };

    const handleProcess = async () => {
        if (!file) return;

        setProcessing(true);
        setError(null);

        try {
            await ingestArticle(file);
            toast.success('Ingest started successfully');
            setFile(null);
            // Close modal first
            onOpenChange(false);
            // Then trigger success callback (which should refresh data)
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error('Ingestion failed:', err);
            // Parse error message if it's JSON string, otherwise use text
            let msg = 'Failed to ingest article';
            try {
                const jsonErr = JSON.parse(err.message);
                if (jsonErr.detail) {
                    if (typeof jsonErr.detail === 'string') {
                        msg = jsonErr.detail;
                    } else if (Array.isArray(jsonErr.detail)) {
                        // Extract first Pydantic error message
                        msg = jsonErr.detail.map(e => e.msg).join(', ');
                    } else {
                        msg = JSON.stringify(jsonErr.detail);
                    }
                }
            } catch (e) {
                msg = err.message || msg;
            }
            setError(msg);
            toast.error(msg);
        } finally {
            setProcessing(false);
        }
    };

    const resetAndClose = () => {
        setFile(null);
        setError(null);
        setProcessing(false);
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={resetAndClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Upload Articles</DialogTitle>
                    <DialogDescription>
                        Upload a Word document (.docx) to automatically extract and analyze content via the Article Agent.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Helper / Info Area */}
                    <div className="bg-brand-50 p-3 rounded-lg flex gap-3 text-brand-700">
                        <div className="mt-0.5"><FileText className="h-4 w-4" /></div>
                        <div>
                            Using <strong>GPT-4 Pipeline</strong>.
                            <br />
                            The system will automatically generate Title, Summary, Category, and Belief Tags.
                        </div>
                    </div>

                    {/* Governed Dropzone */}
                    <Dropzone
                        onFileSelect={handleFileSelect}
                        file={file}
                        onClear={() => setFile(null)}
                        accept=".docx"
                        processing={processing}
                        error={error}
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={resetAndClose} disabled={processing}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleProcess}
                        disabled={!file || processing}
                        className="bg-brand-900 hover:bg-brand-950 text-white"
                    >
                        {processing && <CircleNotch className="mr-2 h-4 w-4 animate-spin" />} {/* # nolint */}
                        {processing ? 'Processing...' : 'Start Processing'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
